/**
 * The orientation tour — what it points at, in order (TUTORIAL-STATE-001).
 *
 * DATA, NOT JSX, on purpose. The whole risk in a product tour is that it points
 * at something that is not on the screen, and that is a question about the
 * running DOM rather than about React. Keeping the steps as a list means the
 * resolver in `useTutorial` can ask "is this anchor present?" for each one and
 * drop the ones that are not, without any step knowing about any other.
 *
 * ── THE THING THAT MAKES THIS HARD ─────────────────────────────────────────
 *
 * The sidebar is filtered three ways before a user sees it:
 *
 *   · by ROLE — `verifyTenant`'s allow-list is default-OFF for non-directors,
 *     so a manager sees only what a director has ticked on
 *   · by TENANT — `tenant.hiddenSections`
 *   · by FEATURE FLAG — several sections are behind one
 *
 * So there is no step list that is correct for everybody. A fixed ten steps
 * would point at empty space for most of the platform's users, and the two
 * roles nobody tests as — `accounts` and `transport_manager` — would get the
 * worst of it. Every step is therefore allowed to be absent, and absence is
 * ordinary rather than an error.
 *
 * ── WHY NOT ROLE PREDICATES ────────────────────────────────────────────────
 *
 * The obvious alternative is `roles: ['director', 'admin']` on each step. It
 * was rejected: the role is not what decides whether the nav item is there —
 * the allow-list, the tenant's hidden sections, the sector and the feature flag
 * all get a say, and none of them is readable from the token. A step list keyed
 * on role would claim to know something it cannot know, and would be wrong in
 * the direction that shows somebody a spotlight on nothing. Asking the DOM is
 * both simpler and actually correct.
 *
 * ── ANCHORS ────────────────────────────────────────────────────────────────
 *
 * `data-tutorial="…"` attributes, added at the point the element is rendered.
 * Not CSS classes (they are not what they look like on this platform — see the
 * `.primary` note in theme.css) and not `data-testid`, which barely exists in
 * `Sidebar.tsx` and means something else. The sidebar sets one on EVERY nav
 * entry, derived from its `labelKey`, so this list can name any of them without
 * the sidebar needing to know which are used.
 */

export interface TutorialStep {
  /** Stable id. Stored as `lastStepKey`, so renaming one costs a resume. */
  key: string;
  /**
   * `data-tutorial` value to spotlight. Omitted for a step that is about the
   * platform rather than about a control — those render centre-screen.
   */
  anchor?: string;
  /**
   * First anchor in the list that is actually present wins.
   *
   * Used for the "this is your day-to-day" step, where the right thing to point
   * at depends entirely on what the person does. A fixed sequence of
   * section steps would be ten steps for a director and one for an accounts
   * user; this keeps the tour the same length for everybody and always points
   * at something they can use.
   */
  anchorAny?: readonly string[];
  /** Route to be on for this step. Omitted means "wherever we already are". */
  route?: string;
  /** i18n key under `tutorial:steps`. */
  i18nKey: string;
}

/**
 * The sections to try for the "your day-to-day" step, most-likely first.
 *
 * Ordering is a judgement about what a new user is most often here to do, not a
 * ranking of the sections: HR and Health & Safety are the two every tenant has,
 * and the rest are the verticals in the order they tend to be the reason
 * somebody was given a login.
 */
const DAY_TO_DAY_ANCHORS = [
  'nav-hr_people',
  'nav-health_safety',
  'nav-rota_shifts',
  'nav-fleet_transport',
  'nav-training_records',
  'nav-payroll',
  'nav-erp_accounts',
  'nav-standard_sales',
  'nav-motor_trade',
  'nav-customer_crm',
] as const;

/**
 * Every `nav-*` anchor this file names, for the test that checks them against
 * the sidebar's own `labelKey`s.
 *
 * That test is the point of this export. The anchors are strings built from a
 * labelKey at the far end of a 2,400-line file, so a rename there breaks the
 * tour with no type error, no test failure and nothing on screen except a step
 * that quietly skips itself. Three of these were wrong when first written —
 * `nav-administration`, `nav-rota` and `nav-fleet`, against real keys of
 * `admin_hub`, `rota_shifts` and `fleet_transport` — and the tour would have
 * run, eight steps instead of ten, looking entirely healthy.
 */
export const NAV_ANCHORS: readonly string[] = [...DAY_TO_DAY_ANCHORS, 'nav-admin_hub'];

/**
 * Ten steps. Two of them (admin, assist) are absent for plenty of users and
 * skip silently, so the tour is eight to ten stops depending on the account —
 * which is the point.
 */
export const ORIENTATION_STEPS: readonly TutorialStep[] = [
  // No anchor: an opening that spotlights a button has nothing to say yet.
  { key: 'welcome', i18nKey: 'welcome' },

  // The nav as a whole, before any single entry in it.
  { key: 'sidebar', anchor: 'sidebar', i18nKey: 'sidebar' },

  { key: 'your_sections', anchorAny: DAY_TO_DAY_ANCHORS, i18nKey: 'your_sections' },

  /*
   * The Guidance Engine, early rather than late. It is the one part of the
   * platform that answers "what am I supposed to do here", and somebody who
   * learns it exists in their first two minutes asks us fewer questions for
   * the rest of the year.
   */
  { key: 'guidance', anchor: 'guidance-engine', i18nKey: 'guidance' },

  /*
   * How-to, immediately after Guidance, because the two sit together in the
   * sidebar and answer DIFFERENT questions — and that difference is the whole
   * reason this step exists. Reagan, 2026-09-24: *"the how to are vids and q
   * and as for how to use jeanie iq platform"*. Guidance is the rules you have
   * to meet; How-to is driving the software you meet them with. Somebody shown
   * the first and not the second assumes the second is more of the same, and
   * never opens it.
   *
   * Added 2026-09-24 at Reagan's request, after main introduced the block.
   */
  { key: 'how_to', anchor: 'how-to', i18nKey: 'how_to' },

  // Absent for anybody without Administration. Skips.
  { key: 'admin', anchor: 'nav-admin_hub', i18nKey: 'admin' },

  { key: 'ask_jeanie', anchor: 'ask-jeanie', i18nKey: 'ask_jeanie' },

  /*
   * RE-POINTED 2026-09-24, from `assist-link` to the Account menu button.
   *
   * It used to spotlight the JeanieIQ Assist control in the header strip.
   * `HEADER-CHROME-001` collapsed Assist and Log out into this dropdown to stop
   * them competing for the strip — so the anchor came to exist only while the
   * menu was OPEN, and a closed menu is the normal state. The step stopped
   * appearing for anybody, with no error: the tour simply ran nine stops and
   * looked healthy. Exactly the failure `show_again` had in September, and the
   * second time the same cause has bitten.
   *
   * The button is always on screen, so this step is no longer skippable — which
   * also means it is no longer the "absent for a tenant with Assist off" case
   * it was written as. That is fine: the menu is worth a stop on its own, since
   * it is where the profile, the subscription and the way out all live.
   */
  { key: 'assist', anchor: 'account-menu', i18nKey: 'assist' },

  { key: 'notifications', anchor: 'notifications', i18nKey: 'notifications' },

  /*
   * `show_again` WAS HERE, and was folded into the Account step above on
   * 2026-09-25.
   *
   * It spotlighted the re-entry button as a stop of its own, on the reasoning
   * that somebody who finishes the tour and cannot find it again has had a
   * one-off — which is still true, and is still said, just in the Account
   * step's words rather than its own.
   *
   * It had to go because the button moved INTO the Account menu, and an anchor
   * inside a closed menu is absent, and an absent anchor skips silently. That
   * is now the third time this shape of fault has come up, so rather than
   * re-point a second step at the same menu button — two stops on one control,
   * back to back — the message moved to the step that already points there.
   *
   * Net effect: ten steps, which is the number Reagan asked for.
   */

  { key: 'done', i18nKey: 'done' },
];

/**
 * How long to wait for a step's anchor to appear before giving up on it.
 *
 * 800ms, down from 1500. The wait is not free: while it runs, the tour holds a
 * dimmed screen with no card, so every absent step costs the user this long
 * staring at nothing. And the anchors this tour uses are in the sidebar and the
 * header, which render with the shell — they are either there in the first
 * frame or they are not there at all. The MutationObserver is what actually
 * catches the slow ones, and it fires the moment they land; this number only
 * decides how long to keep watching.
 */
export const ANCHOR_TIMEOUT_MS = 800;
